import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridRow } from "@/common/styles";
import AuditLogInformation from "./AuditLogInformation";
import { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import { Popconfirm } from "antd";
import { useSelector } from "react-redux";
import { getRefListNameByID, isNonEmptyVariable } from "@/util/common_function";
import DataTable from "@/common/elements/DataTable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const DEFAULT_HEADERS = [
  { key: "systemAuditLogID", text: "ID" },
  { key: "user", text: "UserName" },
  { key: "agency", text: "Agency" },
  { key: "dateAdded", text: "Date Added" },
  { key: "timeAdded", text: "Time Added" },
  { key: "appContext", text: "Module Name" },
  { key: "rootAppContext", text: "rootAC" },
  { key: "rootAppContextKeyFieldID", text: "Root ID" },
  { key: "fullFieldSource", text: "Full Field Source" },
  { key: "action", text: "Action" },
  { key: "appContext", text: "App Context" },
  { key: "extraText", text: "Extra Text" },
];

export const AuditLogEntries = ({ auditLogData }) => {
  const bookings = useSelector((state) => state?.bookings?.bookings);
  // ASP's Audit Log list view is a standard CreateListWithButtons grid,
  // which always gets a Details/Config button pair - Config was missing
  // here even though every other grid in the app already has it via this
  // same hook (see HousandTrackingTable.jsx for the reference pattern).
  // This component is shared across every module's Audit Log tab
  // (Bookings, Civil Papers, etc.), so the fix applies everywhere at once.
  const {
    headers,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("AuditLog:Entries", DEFAULT_HEADERS);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [auditLog, setAuditLog] = useState(bookings?.systemAuditLog);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [selectedRange, setSelectedRange] = useState("last 24 hours");
  const [filteredAuditLog, setFilteredAuditLog] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (isNonEmptyVariable(auditLogData) && auditLogData?.length) {
      const a = auditLogData?.map((entry) => ({
        ...entry,
        user: entry?.creatorStaffID
          ? getRefListNameByID("", entry?.creatorStaffID, true, true)
          : entry?.user,
      }));
      setAuditLog(() => a);
    }
  }, [auditLogData]);

  useEffect(() => {
    if (auditLog?.length) {
      let da = filterLogsByDate(auditLog, selectedRange);

      if (!showTable) {
        da = da?.filter((d) => d?.action?.toLowerCase() !== "view");
      }

      setFilteredAuditLog(() => da);
    } else {
      setFilteredAuditLog(() => []);
    }
  }, [auditLog, selectedRange, showTable]);

  const handleDetailsModal = () => {
    setShowDetailsModal(!showDetailsModal);
  };

  const handleCheckboxChange = (e) => {
    setShowTable(e.target.checked);
  };

  const handleRangeChange = (value) => {
    setSelectedRange(value);
  };

  const selectedData = (values) => {
    console.log("values: ", values);
    if (isNonEmptyVariable(values))
      setSelectedAudit(() => ({
        ...values,
        action: values?.action,
        module: values?.appContext,
        moduleID: values?.rootAppContextKeyFieldID,
        rootAppContext: values?.rootAppContext,
        record: bookings?.systemAuditLog?.[0]?.recordNumber,
        tablePath: values?.fullFieldSource,
        tableID: values?.rootAppContextKeyFieldID,
        extraText: values?.extraText,
        id: values?.systemAuditLogId,
        dateEntered: values?.dateAdded,
        timeEntered: values?.timeAdded,
        user: values?.user,
      }));
  };

  const getSummaryData = (entries) => {
    if (!entries) return [];
    const uniqueEntries = {};

    entries.forEach((entry) => {
      if (!uniqueEntries[entry.extraText]) {
        uniqueEntries[entry.extraText] = entry;
      }
    });

    return Object.values(uniqueEntries);
  };

  const filterLogsByDate = (entries, range) => {
    if (!entries) return [];
    const now = new Date();

    let fromDate;
    switch (range) {
      case "last 24 hours":
        fromDate = new Date(now.setDate(now.getDate() - 1));
        break;
      case "last 2 weeks":
        fromDate = new Date(now.setDate(now.getDate() - 14));
        break;
      case "last 4 weeks":
        fromDate = new Date(now.setDate(now.getDate() - 28));
        break;
      case "last 6 weeks":
        fromDate = new Date(now.setDate(now.getDate() - 42));
        break;
      case "summary":
        return getSummaryData(entries);
      case "all entries":
        return entries;
      default:
        return [];
    }

    return entries.filter((entry) => {
      const entryDate = new Date(entry.dateAdded);
      return entryDate >= fromDate;
    });
  };

  const handleChangePagination = (pageIndex, pageSize) => {
    setTablePagination({
      pageIndex,
      pageSize,
    });
  };

  return (
    <>
      <StyledFlex gap="20px">
        <div style={{ minWidth: "300px" }}>
          <FormItem
            label="See entries for"
            layout="horizontal"
            style={{ marginBottom: "0" }}
          >
            <Select
              defaultValue="Last 24 Hours"
              onChange={handleRangeChange}
              options={[
                {
                  value: "all entries",
                  label: "All Entries",
                },
                {
                  value: "last 2 weeks",
                  label: "Last 2 Weeks",
                },
                {
                  value: "last 24 hours",
                  label: "Last 24 Hours",
                },
                {
                  value: "last 4 weeks",
                  label: "Last 4 Weeks",
                },
                {
                  value: "last 6 weeks",
                  label: "Last 6 Weeks",
                },
                {
                  value: "summary",
                  label: "Summary",
                },
              ]}
            />
          </FormItem>
        </div>
        <FormItem
          label="Show View log entries?"
          layout="horizontal"
          labelPostion="label-right"
          style={{ marginBottom: "0" }}
        >
          <Checkbox onChange={handleCheckboxChange} />
        </FormItem>
      </StyledFlex>

      <StyledFlex gap="20px" style={{ marginBottom: "15px" }} justify="end">
        <Button
          onClick={handleDetailsModal}
          disabled={selectedAudit === null || isConfiguring}
        >
          View Details
        </Button>

        {!isConfiguring ? (
          <Button htmlType="button" onClick={startConfiguring}>
            {configButtonLabel}
          </Button>
        ) : (
          <Popconfirm
            title="Do you want to save the current list view configuration?"
            onConfirm={confirmSave}
            onCancel={discardChanges}
            okText="Yes"
            cancelText="No"
          >
            <Button htmlType="button">{configButtonLabel}</Button>
          </Popconfirm>
        )}
      </StyledFlex>

      <DataTable
        ref={dataTableRef}
        // style={{ marginBottom: "15px" }}
        headers={headers}
        draggable={isConfiguring}
        data={filteredAuditLog}
        onRowClick={selectedData}
        totalRecords={filteredAuditLog?.length}
        rowUniqueId={"systemAuditLogID"}
        pagination={pagination}
        setPagination={setPagination}
        handleChangePagination={handleChangePagination}
        allowTableSorting={true}
      />
      <StyledGridRow></StyledGridRow>

      {showDetailsModal && (
        <Modal
          size="xl"
          header="Audit Log Entry Details"
          show={showDetailsModal}
          onHide={handleDetailsModal}
          footerCloseAction
          footerCloseActionText="Close"
          footerActionsNeed={false}
        >
          <AuditLogInformation formData={selectedAudit} />
        </Modal>
      )}
    </>
  );
};
export default AuditLogEntries;
